/**
 * KitScripture.tsx
 *
 * Bible widget
 */
import { ImageServiceType } from '@omni/kit/Types';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { parseSearchResults } from '@omni/kit/services/SearchService/parseSearchResults';
import { parseImageUrl } from '@omni/kit/utilities/utilities';
import Axios from 'axios';
import Color from 'color';
import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Dimensions,
  GestureResponderEvent,
  Image,
  LayoutAnimation,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import RenderHTML, { MixedStyleDeclaration } from 'react-native-render-html';

import { IMediaItemData } from '../../feeds/mediaItemTypes';
import PassageService from '../../services/Bible/PassageService';
import SearchService from '../../services/SearchService';
import BorderRadius from '../../theming/BorderRadius';
import Colors from '../../theming/Colors';
import Spacing from '../../theming/Spacing';
import { ThemeContext } from '../../theming/ThemeContext';
import ImageIndicator from '../ImageIndicator';
import KitError from '../KitError';
import KitListItem from '../KitListItem';
import KitLoader from '../KitLoader';
import KitText from '../KitText';
import Show from '../Show';
import {
  getFullChapterOsis,
  getSinglePassageFullChapter,
  isFullChapter,
  isSinglePassageFullChapter,
} from './osisUtils';
import { Props, VerseLocation } from './types';

const debug = require('debug')('tca:kit:component:KitScripture');

const IMAGE_SIZE = 80;
const SILHOUETTE_IMAGE_SIZE = 20;
interface HTMLNode {
  type: string;
  name?: string;
  attribs?: Record<string, string>;
  children?: HTMLNode[];
  data?: string;
}

export default function KitScripture(props: Props): ReactElement {
  const {
    appKey = '',
    bibleContent = '',
    bookName = '',
    disableLayoutAnimation = false,
    enableHighlights,
    enableSelection,
    footerComponent,
    highlightedVerses,
    initialSelected = {},
    onRelatedMediaPress,
    osis,
    relatedMediaTitle = '',
    showBorder = false,
    showReferenceForOnePassage = false,
    showRelatedMedia = false,
    showTitles = true,
    silhouetteLogoImage = '',
    updateSelectedVerses,
    containerStyle,
  } = props;

  const { colorForScheme } = useContext(ThemeContext);
  const { t } = useTranslation();

  //**************************************************************************
  // State Values
  //**************************************************************************

  const [bibleContentState, setBibleContentState] = useState({
    content: bibleContent,
    contentLoaded: bibleContent.length !== 0,
    contentOpacity: bibleContent.length === 0 ? 0 : 1,
  });
  const [errorCode, setErrorCode] = useState<number | null>(null);

  const [selectedVerses, setSelectedVerses] = useState(initialSelected);
  const [relatedMedia, setRelatedMedia] = useState<IMediaItemData | null>(null);
  const [relatedMediaTotal, setRelatedMediaTotal] = useState<number>(0);

  // Navigation state for chapter reading
  const [prevOsis, setPrevOsis] = useState<string | null>(null);
  const [prevPassageReference, setPrevPassageReference] = useState<
    string | null
  >(null);
  const [prevScrollPosition, setPrevScrollPosition] = useState<number>(0);
  const [scrollViewRef, setScrollViewRef] = useState<ScrollView | null>(null);
  const [currentOsis, setCurrentOsis] = useState<string>(osis || '');

  //**************************************************************************
  // Methods
  //**************************************************************************

  const onLinkPress = (
    _event: GestureResponderEvent,
    _link: string,
    htmlAttribs: Partial<VerseLocation> & { verseText?: string } & {
      'data-action'?: string;
      'data-osis'?: string;
      'data-display-text'?: string;
    }
  ) => {
    // Handle navigation actions
    if (htmlAttribs['data-action'] === 'read-full-chapter') {
      const osisRef = htmlAttribs['data-osis'];
      const displayReference = htmlAttribs['data-display-text'];

      if (osisRef) {
        handleReadFullChapterForPassage(osisRef, displayReference);
      } else {
        handleReadFullChapter();
      }

      return;
    }

    if (htmlAttribs['data-action'] === 'go-back') {
      handleGoBack();

      return;
    }

    // Default verse press behavior
    onVersePress(htmlAttribs);
  };

  const onVersePress = (
    htmlAttribs: Partial<VerseLocation> & { verseText?: string }
  ) => {
    if (props.onPress) {
      props.onPress();
    }

    if (!enableSelection && !enableHighlights) {
      return;
    }

    debug('html attribs', htmlAttribs);
    const { book, chapter, verse, verseText } = htmlAttribs as VerseLocation & {
      verseText: string;
    };
    const verseId = `${book}-${chapter}-${verse}`;
    const newVerses = { ...selectedVerses };

    if (newVerses[verseId]) {
      delete newVerses[verseId];
    } else {
      newVerses[verseId] = {
        id: verseId,
        location: { book, chapter, verse },
        text: verseText,
      };
    }

    setSelectedVerses(newVerses);
    // Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  };

  const customDOMVisitors = {
    onElement: (element: HTMLNode) => {
      const { name } = element;

      // 1. Add verse text to <a> tag
      if (name === 'a') {
        try {
          element.attribs = {
            ...(element.attribs || {}),
            verseText: ((element.children?.[1] as HTMLNode).data || '').trim(),
          };
        } catch {}
      }

      // 2. Style superscripts
      if (name === 'sup') {
        element.attribs = {
          ...(element.attribs || {}),
          style: `color: ${colorForScheme?.({
            default: Colors.N500,
          })};font-size: 10px;`,
        };
      }

      // 3. Apply selection or highlight styles
      let addedStyle = '';

      if (enableSelection && verseIsSelected(element)) {
        addedStyle += `
          background-color: ${colorForScheme?.({
            light: Colors.N100,
            dark: Colors.N500,
          })};
          text-decoration-line: underline;
        `;
      }

      if (enableHighlights && verseIsHighlighted(element)) {
        let highlightColor = '#ffefb7';
        const { book, chapter, verse } = element.attribs || {};

        const key = `${book}-${chapter}-${verse}`;
        highlightColor = highlightedVerses?.[key] ?? highlightColor;

        highlightColor =
          colorForScheme?.({
            light: highlightColor,
            dark: Color(highlightColor).darken(0.5).hex(),
          }) || highlightColor;

        addedStyle += `background-color: ${highlightColor};`;
      }

      if (name === 'a') {
        element.attribs = {
          ...(element.attribs || {}),
          style: `${addedStyle}${element.attribs?.style || ''}`,
        };
      }

      if (name === 'span') {
        const originalStyle = element.attribs?.style || '';

        if (originalStyle.includes('color: red')) {
          element.attribs = {
            ...element.attribs,
            style: `${originalStyle};color: ${colorForScheme?.({
              light: 'red',
              dark: Colors.R500,
            })};font-weight:500;`,
          };
        }
      }
    },
  };

  const verseIsSelected = (node: HTMLNode): boolean => {
    if (!node.attribs || !enableSelection) {
      return false;
    }

    const { book, chapter, verse } = node.attribs;

    return Boolean(selectedVerses[`${book}-${chapter}-${verse}`]);
  };

  const verseIsHighlighted = (node: HTMLNode): boolean => {
    if (!node.attribs || !enableHighlights) {
      return false;
    }

    const { book, chapter, verse } = node.attribs;

    return Boolean(highlightedVerses?.[`${book}-${chapter}-${verse}`]);
  };

  const restoreScrollPosition = () => {
    if (scrollViewRef && prevScrollPosition > 0) {
      scrollViewRef.scrollTo({ y: prevScrollPosition, animated: false });
    }
  };

  const handleReadFullChapter = () => {
    if (currentOsis && !isFullChapter(currentOsis)) {
      setPrevOsis(currentOsis);
      // For direct bibleContent, we don't have a meaningful passage reference
      setPrevPassageReference(null);
      // Current scroll position will be saved via onScroll event

      const fullChapterOsis = getFullChapterOsis(currentOsis);
      setCurrentOsis(fullChapterOsis);

      // Also notify parent if callback is provided
      if (props.onOsisChange) {
        props.onOsisChange(fullChapterOsis);
      }
    }
  };

  const handleReadFullChapterForPassage = (
    osisRef: string,
    displayReference?: string
  ) => {
    if (osisRef && !isSinglePassageFullChapter(osisRef)) {
      setPrevOsis(currentOsis);
      // Store the specific passage reference that was clicked, not all passages
      setPrevPassageReference(displayReference || null);
      // Current scroll position will be saved via onScroll event

      const fullChapterOsis = getSinglePassageFullChapter(osisRef);
      setCurrentOsis(fullChapterOsis);

      // Also notify parent if callback is provided
      if (props.onOsisChange) {
        props.onOsisChange(fullChapterOsis);
      }
    }
  };

  const handleGoBack = () => {
    if (prevOsis) {
      const osisToRestore = prevOsis;
      setPrevOsis(null);
      setPrevPassageReference(null);
      setCurrentOsis(osisToRestore);

      // Also notify parent if callback is provided
      if (props.onOsisChange) {
        props.onOsisChange(osisToRestore);
      }

      // Restore scroll position after content loads
      setTimeout(() => {
        restoreScrollPosition();
        setPrevScrollPosition(0);
      }, 300);
    }
  };

  const createReadFullChapterLink = (
    osisRef?: string,
    displayReference?: string
  ): string => {
    return `
      <div style="margin-top: ${Spacing.m};">
        <a href="#" onclick="return false;" data-action="read-full-chapter" ${
          osisRef ? `data-osis="${osisRef}"` : ''
        } ${
      displayReference ? `data-display-text="${displayReference}"` : ''
    } style="
          color: ${textColor};
          text-decoration: underline;
          font-weight: 500;
          font-size: ${Platform.OS === 'ios' ? 16 : 15}px;
          font-family: ${
            Platform.OS === 'ios'
              ? 'NewYorkMedium-Regular'
              : 'NotoSerif-Regular'
          };
          line-height: 30.5px;
        ">${t('bible:readFullChapter')}</a>
      </div>
    `;
  };

  const createGoBackLink = (position: 'top' | 'bottom'): string => {
    const marginStyle =
      position === 'top'
        ? `margin-bottom: ${Spacing.l}`
        : `margin-top: ${Spacing.xl}`;

    const backText = prevPassageReference
      ? t('bible:backToPassage', { passage: prevPassageReference })
      : t('bible:backToPreviousPassage');

    return `
      <div style="${marginStyle};">
        <a href="#" onclick="return false;" data-action="go-back" style="
          color: ${textColor};
          text-decoration: underline;
          font-weight: 500;
          font-size: ${Platform.OS === 'ios' ? 16 : 15}px;
          font-family: ${
            Platform.OS === 'ios'
              ? 'NewYorkMedium-Regular'
              : 'NotoSerif-Regular'
          };
          line-height: 30.5px;
        ">${backText}</a>
      </div>
    `;
  };

  const addNavigationLinks = (html: string): string => {
    let modifiedHtml = html;

    // Add "Go back" link at the top if we have a previous osis
    if (prevOsis) {
      modifiedHtml = createGoBackLink('top') + modifiedHtml;
    }

    // Only add general "Read full chapter" link if content wasn't fetched with individual links
    // This handles cases where bibleContent is provided directly via props
    if (currentOsis && !isFullChapter(currentOsis) && bibleContent) {
      modifiedHtml += createReadFullChapterLink();
    }

    // Add "Go back" link at the bottom if we have a previous osis
    if (prevOsis) {
      modifiedHtml += createGoBackLink('bottom');
    }

    return modifiedHtml;
  };

  const fetchContent = async (): Promise<void> => {
    try {
      if (currentOsis) {
        const result = await PassageService.getAll({
          filters: [
            ['translation_code', props.version],
            ['osis', currentOsis],
          ],
        });

        const passages = result.body?._embedded.passages;

        const status = result.status || 550;

        setErrorCode(status >= 400 ? status : null);

        if (passages) {
          let html = '';

          if (passages.length > 1) {
            passages.forEach((passage, i) => {
              if (showBorder && i) {
                html += `<div style="width: 100%; height: 1px; background-color:${
                  colorForScheme?.({
                    light: Colors.N300,
                    dark: Colors.N500,
                  }) || Colors.N300
                }; margin-top: ${Spacing.xxl}; margin-bottom: ${
                  Spacing.xxl
                };"></div>`;
              }

              html += `<h3>${passage.reference}</h3>${passage.content_html}`;

              // Add "Read full chapter" link after each passage if it's not a full chapter
              if (passage.osis && !isSinglePassageFullChapter(passage.osis)) {
                html += createReadFullChapterLink(
                  passage.osis,
                  passage.reference
                );
              }
            });
          } else if (passages.length === 1) {
            const passage = passages[0];

            if (showReferenceForOnePassage) {
              html += `<h3>${passage.reference}</h3>`;
            }

            html += passage.content_html;

            // Add "Read full chapter" link after single passage if it's not a full chapter
            if (passage.osis && !isSinglePassageFullChapter(passage.osis)) {
              html += createReadFullChapterLink(
                passage.osis,
                passage.reference
              );
            }
          }

          if (!disableLayoutAnimation) {
            LayoutAnimation.configureNext(
              LayoutAnimation.Presets.easeInEaseOut
            );
          }

          // Add navigation links to the HTML content
          const htmlWithNavigation = addNavigationLinks(html);

          setBibleContentState({
            content: htmlWithNavigation,
            contentLoaded: true,
            contentOpacity: 1,
          });
        }
      }
    } catch (err: any) {
      if (Axios.isCancel(err)) {
        debug('Bible fetch cancelled.', currentOsis);
      } else {
        console.warn('Caught error fetching Bible content: ', err.message);
      }
    }
  };

  const fetchRelatedMedia = async (): Promise<void> => {
    try {
      if (!currentOsis) return;

      const response = await SearchService.search({
        appFilterValue: appKey,
        page: 1,
        size: 1,
        type: ['media-item'],
        scripture: currentOsis,
        getFromCache: false,
      });

      if (response.body) {
        const relatedMediaItems = parseSearchResults(response);
        setRelatedMediaTotal(response.body.total);

        if (relatedMediaItems.length) {
          setRelatedMedia(relatedMediaItems[0] as IMediaItemData);
        }
      }
    } catch (e) {
      debug('Bible fetch related media failed.', e);
    }
  };

  const textColor = colorForScheme?.({ light: Colors.N900, dark: Colors.N0 });
  const defaultContainerStyle = {
    paddingBottom: Spacing.xl,
  };

  const tagsStyles: Readonly<Record<string, MixedStyleDeclaration>> = {
    a: {
      color: textColor,
      textDecorationLine: 'none' as const,
    },
    h3: showTitles
      ? {
          marginTop: Spacing.m,
          marginBottom: Spacing.xl,
          fontFamily: Platform.OS === 'android' ? 'Roboto-Bold' : undefined,
          fontWeight: '700' as const,
        }
      : {
          display: 'none' as const,
        },
    h4: {
      color: colorForScheme?.({ light: Colors.N500, dark: Colors.N300 }),
      fontSize: 16,
      fontStyle: 'italic' as const,
      fontWeight: '300' as const,
      marginBottom: Spacing.m,
    },
    p: {
      marginBottom: Spacing.m,
      fontFamily:
        Platform.OS === 'ios' ? 'NewYorkMedium-Regular' : 'NotoSerif-Regular',
    },
  };
  const baseFontStyle: MixedStyleDeclaration = {
    color: textColor,
    fontSize: Platform.OS === 'ios' ? 16 : 15,
    letterSpacing: 0,
    lineHeight: 30.5,
    ...(props.baseFontStyle as MixedStyleDeclaration),
  };

  const { content, contentLoaded, contentOpacity } = bibleContentState;
  const _renderBibleHtml = useMemo(() => {
    if (content) {
      /** Don't render html view unless we've finished fetching html content. */
      return (
        <RenderHTML
          // CRITICAL: This key prop is required for Android to properly update verse highlights.
          // Android's rendering engine caches the processed DOM tree more aggressively than iOS,
          // which causes stale highlight/underline styles to persist when verses are unselected.
          // By changing the key when selectedVerses changes, we force React to completely remount
          // the component, ensuring Android reprocesses the DOM with fresh styles.
          // DO NOT REMOVE - Without this, unselecting verses will leave visual highlights on Android.
          key={Object.keys(selectedVerses).sort().join(',')}
          domVisitors={customDOMVisitors}
          baseStyle={baseFontStyle}
          contentWidth={Dimensions.get('window').width}
          classesStyles={classesStyles}
          source={{ html: content }}
          enableCSSInlineProcessing={true}
          renderersProps={{
            a: {
              onPress: (_evt, _href, htmlAttribs) => {
                onLinkPress(_evt, _href, htmlAttribs);
              },
            },
          }}
          tagsStyles={tagsStyles}
          defaultTextProps={{
            selectable: false,
            allowFontScaling: true,
          }}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, selectedVerses]);

  //**************************************************************************
  // Lifecycle
  //**************************************************************************

  // Update currentOsis when osis prop changes
  useEffect(() => {
    if (osis !== currentOsis) {
      setCurrentOsis(osis || '');
      // Reset navigation state when osis prop changes externally
      setPrevOsis(null);
      setPrevPassageReference(null);
      setPrevScrollPosition(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osis]);

  // Reset content state when currentOsis changes
  useEffect(() => {
    debug('currentOsis: ', currentOsis);
    if (currentOsis) {
      // Reset content state before fetching new content
      setBibleContentState({
        content: '',
        contentLoaded: false,
        contentOpacity: 0,
      });
    }
  }, [currentOsis]);

  // Fetch content when we have currentOsis but no content or content isn't loaded
  useEffect(() => {
    if (
      currentOsis &&
      (!bibleContentState.content || !bibleContentState.contentLoaded)
    ) {
      fetchContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOsis, bibleContentState.content, bibleContentState.contentLoaded]);

  useEffect(() => {
    fetchRelatedMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOsis, showRelatedMedia]);

  useEffect(() => {
    if (enableSelection && updateSelectedVerses) {
      updateSelectedVerses(selectedVerses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVerses]);

  return (
    <View style={props.style}>
      <Show show={Boolean(!contentLoaded && errorCode === null)}>
        <View
          style={{
            alignItems: 'center',
            bottom: 0,
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <KitLoader />
        </View>
      </Show>

      <ScrollView
        ref={(ref) => setScrollViewRef(ref)}
        onContentSizeChange={(contentWidth, contentHeight) => {
          if (contentLoaded && props.onContentSizeChange) {
            props.onContentSizeChange(contentWidth, contentHeight);
          }
        }}
        onMomentumScrollEnd={props.onMomentumScrollEnd}
        onScroll={(event) => {
          // Save current scroll position for navigation
          const scrollY = event.nativeEvent.contentOffset.y;

          if (scrollY > 0) {
            setPrevScrollPosition(scrollY);
          }

          // Call the original onScroll handler if provided
          if (props.onScroll) {
            props.onScroll(event);
          }
        }}
        scrollEventThrottle={500}
        style={[
          {
            opacity: contentOpacity,
          },
          props.scrollViewStyle,
        ]}
      >
        <Show show={Boolean(bookName)}>
          <KitText
            h1
            color={colorForScheme?.({
              light: Colors.N900,
              dark: Colors.N0,
            })}
            style={{ marginBottom: Spacing.m, marginTop: Spacing.l }}
          >
            {bookName}
          </KitText>
        </Show>
        <Show show={Boolean(showRelatedMedia && relatedMedia)}>
          <KitListItem
            bottomBorder={false}
            ImageComponent={
              <RelatedMediaImage
                averageHexColor={relatedMedia?.averageHexColor}
                backgroundColor={colorForScheme?.({
                  light: Colors.N150,
                  dark: Colors.N700,
                })}
                image={relatedMedia?.image}
                logoImage={silhouetteLogoImage}
              />
            }
            isTitleBold={true}
            onPress={() =>
              onRelatedMediaPress?.(
                relatedMediaTotal,
                relatedMedia as IMediaItemData,
                currentOsis as string,
                bookName
              )
            }
            onPressUnderlayColor={colorForScheme?.({
              light: Colors.N100,
              dark: Colors.N700,
            })}
            showRightIconOnAndroid={true}
            style={{
              ...styles.relatedMediaContainer,
              backgroundColor: colorForScheme?.({
                light: Colors.N0,
                dark: Colors.N1000,
              }),
              borderColor: colorForScheme?.({
                light: Colors.N100,
                dark: Colors.N700,
              }),
            }}
            subtitle={relatedMedia?.scriptures}
            subtitleColor={colorForScheme?.({
              light: Colors.N500,
              dark: Colors.N300,
            })}
            subtitleFontSize={16}
            title={relatedMediaTitle}
            titleColor={colorForScheme?.({
              light: Colors.N1000,
              dark: Colors.N0,
            })}
            titleFontSize={16}
          />
        </Show>
        {_renderBibleHtml}
        {footerComponent}
      </ScrollView>

      <Show show={Boolean(!contentLoaded && errorCode !== null)}>
        <View
          style={{
            alignItems: 'center',
            bottom: 0,
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <KitError
            errorCode={errorCode as number}
            onRetry={() => {
              setErrorCode(null);
              fetchContent();
            }}
          />
        </View>
      </Show>
    </View>
  );
}

//****************************************************************************
// Styles
//****************************************************************************

const classesStyles: Record<string, any> = {
  smallcaps: {
    fontVariant: 'small-caps',
  },
};

interface RelatedMediaImageProps {
  averageHexColor?: string;
  backgroundColor?: string;
  image?: string;
  logoImage?: string;
}

const RelatedMediaImage = ({
  averageHexColor = '',
  backgroundColor = '',
  image = '',
  logoImage = '',
}: RelatedMediaImageProps) => {
  return (
    <View
      style={[
        {
          backgroundColor: averageHexColor || backgroundColor,
        },
        styles.leftContent,
      ]}
    >
      <Show show={Boolean(logoImage)}>
        <Image
          resizeMode='cover'
          source={{
            uri: parseImageUrl(
              logoImage,
              SILHOUETTE_IMAGE_SIZE,
              SILHOUETTE_IMAGE_SIZE,
              ImageServiceType.ImagePng,
              'fit-white'
            ),
            height: SILHOUETTE_IMAGE_SIZE,
            width: SILHOUETTE_IMAGE_SIZE,
          }}
          style={styles.silhouette}
        />
      </Show>
      <Show show={Boolean(image)}>
        <ImageIndicator
          image={image}
          imageHeight={IMAGE_SIZE}
          imageRadius={BorderRadius.s}
          imageWidth={IMAGE_SIZE}
          style={styles.image}
        />
      </Show>
    </View>
  );
};

const styles = StyleSheet.create({
  relatedMediaContainer: {
    borderRadius: Spacing.l,
    borderWidth: 1,
    marginVertical: Spacing.m,
    padding: Spacing.l,
  },
  image: {
    bottom: 0,
    borderRadius: BorderRadius.s,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  leftContent: {
    alignItems: 'center',
    borderRadius: BorderRadius.s,
    height: 48,
    justifyContent: 'center',
    width: 85,
  },
  silhouette: {
    opacity: 0.3,
  },
});
